import React from 'react'
import { t } from 'i18next'
import { api } from './apiHelper'
import { TableColumnDefinition } from '@fluentui/react-components'
import { Activity, ConsequenceAndProbabilityRisk, DemandAnalysisDashboardDTO, FinancialYearDashboardGraphDTO, Workplace } from '../api/schemas/schema'
import { ActivityStatusBadge } from '../components/dashboard/ActivityStatusBadge'
import { IDataContext } from '../interfaces/IDataContext'
import { handleMessage } from './stateHelper'
import { compareByDate, formatDate } from './stringHelper'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

dayjs.extend(utc)
dayjs.extend(isSameOrAfter)

export const getLatestActivities = async (dataContext: IDataContext, setActivities: React.Dispatch<React.SetStateAction<Activity[]>>, signal?: AbortSignal) => {
   const { organisation } = dataContext.state
   try {
      const activites = (await api().getLatestActivites(organisation.id, { signal })).data

      if (activites) {
         // Store the full list untouched. getSortedLatestActivities is applied at render time in
         // ActivitiesCard instead, so period filtering can reach every activity regardless of status.
         setActivities(activites)
      }
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('Activities') }))
   }
}

export const getDemandAnalysisDashboardData = async (
   dataContext: IDataContext,
   setDashboardData: React.Dispatch<React.SetStateAction<DemandAnalysisDashboardDTO>>,
   signal?: AbortSignal
) => {
   const { organisation } = dataContext.state
   try {
      const data = (await api().getDemandAnalysisDashboardData(organisation.id, { signal })).data

      setDashboardData(data)
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('OverviewData') }))
   }
}

export const getAllWorkplaces = async (dataContext: IDataContext, setWorkplaces: React.Dispatch<React.SetStateAction<Workplace[]>>, signal?: AbortSignal) => {
   const { organisation } = dataContext.state
   try {
      const workplaces = (await api().getWorkplacesByOrganisationId(organisation.id, { signal })).data

      if (workplaces) {
         setWorkplaces(workplaces)
      }
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('Workplaces') }))
   }
}

export const getLatestFinancialYears = async (
   dataContext: IDataContext,
   setLatestFinancialYears: React.Dispatch<React.SetStateAction<FinancialYearDashboardGraphDTO[]>>,
   signal?: AbortSignal
) => {
   const { organisation } = dataContext.state
   try {
      const latestFinancialYears = (await api().getLatestFinancialYears(organisation.id, { signal })).data

      if (latestFinancialYears) {
         setLatestFinancialYears(latestFinancialYears)
      } else {
         setLatestFinancialYears([])
      }
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('FinancialYear') }))
   }
}

export const getLatestSwotAnalysis = async (
   dataContext: IDataContext,
   setLatestSwotAnalysis: React.Dispatch<React.SetStateAction<FinancialYearDashboardGraphDTO>>,
   signal?: AbortSignal
) => {
   try {
      const { organisation } = dataContext.state
      const latestSwotAnalysis = (await api().getLatestSwotAnalysis(organisation.id, { signal })).data

      setLatestSwotAnalysis(latestSwotAnalysis)
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('SWOTAnalysis') }))
   }
}
export const getBiggestRisks = async (dataContext: IDataContext, setBiggestRisks: React.Dispatch<React.SetStateAction<ConsequenceAndProbabilityRisk[]>>, signal?: AbortSignal) => {
   try {
      const { organisation } = dataContext.state

      const biggestRisks = (await api().getBiggestRisks(organisation.id, { signal })).data

      setBiggestRisks(biggestRisks)
   } catch (error: any) {
      if (error?.name === 'AbortError') return
      handleMessage(dataContext.setRootState, 'error', t('CouldNotFetchItem', { Item: t('BiggestRisks') }))
   }
}

export const getSortedLatestActivities = (activities: Activity[]) => {
   const today = dayjs().startOf('day')

   const activeActivities = activities
      .filter((activity) => activity.endDate && dayjs.utc(activity.endDate).local().isSameOrAfter(today))
      .sort((a, b) => compareByDate(a.startDate, b.startDate))

   const inactiveActivities = activities
      .filter((activity) => activity.endDate && dayjs.utc(activity.endDate).local().isBefore(today))
      .sort((a, b) => compareByDate(a.startDate, b.startDate))

   if (activeActivities.length >= 12) {
      return activeActivities.slice(0, 12)
   }

   return [...activeActivities, ...inactiveActivities]
}

export const getDashboardActiviyColumns = (): TableColumnDefinition<Activity>[] => [
   {
      columnId: 'status',
      renderHeaderCell: () => '',
      renderCell: (item) => <ActivityStatusBadge item={item} />,
      compare: () => 0,
   },
   {
      columnId: 'title',
      renderHeaderCell: () => t('Activity'),
      renderCell: (item) => item.title,
      compare: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
   },
   {
      columnId: 'startDate',
      renderHeaderCell: () => t('StartDate'),
      renderCell: (item) => formatDate(item.startDate),
      compare: (a, b) => compareByDate(a.startDate, b.startDate),
   },
   {
      columnId: 'endDate',
      renderHeaderCell: () => t('EndDate'),
      renderCell: (item) => formatDate(item.endDate),
      compare: (a, b) => compareByDate(a.endDate, b.endDate),
   },
]

export const getBiggestRisksColumns = (): TableColumnDefinition<ConsequenceAndProbabilityRisk>[] => [
   {
      columnId: 'risk',
      renderHeaderCell: () => null,
      renderCell: (item) => item.scenario,
      compare: () => -1,
   },
]
